import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@nexus/ui";
import { toast } from "sonner";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { usePermissions } from "@/features/auth/hooks/usePermissions";
import { normalizedPreflightTenantId } from "@/lib/queries";
import { deleteReport } from "../../api/preflight.api";
import type { PreflightReportSummary } from "../../types/preflight.types";
import {
  bumpMarketReportCount,
  invalidateMarketAggregates,
} from "../../lib/market-cache";
import { removeReportFromCaches } from "../../lib/report-cache";

export interface DeleteReportDialogProps {
  report: Pick<PreflightReportSummary, "id" | "name" | "market">;
  /** Custom trigger; defaults to a ghost destructive "Delete" button. */
  trigger?: React.ReactNode;
  /** Called after a successful delete — e.g. navigate back to the Hub from the Report view. */
  onDeleted?: () => void;
}

/**
 * Permission-gated destructive-confirm delete (report-ui R11, KTD13). `@nexus/ui` has no
 * AlertDialog, so this is a `Dialog` with a clearly-marked destructive confirm button.
 */
export function DeleteReportDialog({
  report,
  trigger,
  onDeleted,
}: DeleteReportDialogProps) {
  const { t } = useTranslation();
  const permissions = usePermissions();
  const queryClient = useQueryClient();
  const activeTenantId = useAuth((state) => state.activeTenantId);
  const [open, setOpen] = useState(false);

  const mutation = useMutation({
    mutationFn: () => deleteReport(report.id),
    onSuccess: () => {
      const tenantId = normalizedPreflightTenantId(activeTenantId);
      removeReportFromCaches(queryClient, report.id);
      if (report.market?.id) {
        bumpMarketReportCount(queryClient, tenantId, report.market.id, -1);
      }
      invalidateMarketAggregates(queryClient, tenantId);
      setOpen(false);
      toast.success(t("preflight.toast.deleted"));
      onDeleted?.();
    },
    onError: () => toast.error(t("preflight.toast.deleteError")),
    meta: { skipGlobalError: true },
  });

  if (!permissions.PREFLIGHT_DELETE_REPORT.isOn) return null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger ?? (
          <Button type="button" variant="ghost" size="sm" className="text-error">
            {t("preflight.delete.trigger")}
          </Button>
        )}
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("preflight.delete.title")}</DialogTitle>
          <DialogDescription>
            {t("preflight.delete.description", { name: report.name })}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            type="button"
            variant="secondary"
            onClick={() => setOpen(false)}
          >
            {t("preflight.delete.cancel")}
          </Button>
          <Button
            type="button"
            variant="destructive"
            disabled={mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            {mutation.isPending
              ? t("preflight.delete.deleting")
              : t("preflight.delete.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
